import type { DentalOfficeShiftCardMenuItem } from '@tempmee/react-blocks';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import browserHistory from '~/browserHistory';
import { addFavoriteProfessional, showMessage } from '~/actions';
import { useReportIncidentItem } from '~/components/JobList/TodayJobs/ReportIncidentPopup/hooks';
import type { IInviteButtonUser } from '~/components/ProfessionalsHub/interfaces';
import {
  type TInviteButtonSource,
  useRebook,
} from '~/components/ProfessionalsHub/components/useRebook';

interface IUseCurrentShiftCardMenuArgs {
  /** Shift id — chat route + report submission. */
  jobId: string;
  /** Shift start datetime — drives the 30-min report-incident window. */
  shiftStart?: string | Date;
  /** Assigned professional's user id — favorite toggle. */
  professionalId?: string;
  /** Assigned professional's full name — shown in the report-incident popup. */
  professionalName?: string;
  /** Whether an incident was already reported for this shift. */
  alreadyReported?: boolean;
  /**
   * When set, adds a "Rebook" overflow item that re-books the professional (opens the
   * shared invite flow — the new-shift path). Provide the professional (`IInviteButtonUser`),
   * the shift id, and the analytics source.
   */
  rebook?: { user: IInviteButtonUser; shiftId?: string; source?: TInviteButtonSource };
}

const EMPTY_REBOOK_USER = {} as IInviteButtonUser;

/**
 * Assembles the "current shift" / "today's professional" card actions shared by the
 * Schedule List and the dashboard "Today's Professionals" card (GOTU-4588):
 * - `menuItems`: Chat, Rebook (when `rebook` is provided), Add to Favorites (when not
 *   already a favorite)
 * - report-incident wiring for the DS card's warning icon (`onReportIncident` opens the
 *   no-show/walk-out selection popup; `incidentReported` / `canReportIncident` reflect state)
 *
 * Render `rebookPopup` from the card wrapper, and mount a single `<ReportIncidentPopup />`
 * in the list that renders these cards.
 */
export const useCurrentShiftCardMenu = ({
  jobId,
  shiftStart,
  professionalId,
  professionalName,
  alreadyReported = false,
  rebook,
}: IUseCurrentShiftCardMenuArgs) => {
  const dispatch = useDispatch();

  // Rebook = the shared invite flow (new-shift path). The hook is always called (rules of
  // hooks); the item + popup only surface when a rebook professional is provided.
  const canRebook = !!rebook?.user?.id;
  const { newShift, popup: rebookPopup } = useRebook({
    user: rebook?.user ?? EMPTY_REBOOK_USER,
    shiftId: rebook?.shiftId,
    source: rebook?.source,
  });

  // The report hook reads job.shift.start / job.professional.full_name / job.incident.
  const reportJob = useMemo(
    () => ({
      id: jobId,
      incident: alreadyReported || undefined,
      shift: { start: shiftStart },
      professional: { full_name: professionalName },
    }),
    [jobId, alreadyReported, shiftStart, professionalName],
  );
  const {
    isReported,
    isShiftNotStartedYet,
    isShiftStartedLessThan30Minutes,
    onReportIncidentClick,
  } = useReportIncidentItem(reportJob);

  const favoriteProfessionals = useSelector(
    // `favoriteProfessionals` holds professional ids at runtime, though typed as IUser[].
    (state: { user: { user?: { favoriteProfessionals?: unknown } } }) =>
      (state.user.user?.favoriteProfessionals ?? []) as string[],
  );
  const isFavorite = !!professionalId && favoriteProfessionals.includes(professionalId);

  const addFavorite = useCallback(() => {
    if (!professionalId) return;
    dispatch(addFavoriteProfessional(professionalId));
  }, [dispatch, professionalId]);

  // Report click gating mirrors the legacy behaviour: before the shift starts the icon is
  // muted and does nothing; in the first 30 min a "please wait" hint shows; after that the
  // no-show / walk-out selection popup opens.
  const handleReportIncident = useCallback(() => {
    if (isShiftNotStartedYet) return;
    if (isShiftStartedLessThan30Minutes) {
      dispatch(
        showMessage({
          title: 'Please Wait Before Reporting',
          message:
            'Reporting is unavailable for the first 30 minutes after the shift starts. If the ' +
            'professional still hasn’t arrived after that, please return to submit a report. ' +
            'Thank you for your patience.',
          popupProps: {
            rightButtonText: 'Close',
            descriptionStyle: { marginBottom: 40, width: 420 },
          },
        }),
      );
      return;
    }
    onReportIncidentClick();
  }, [dispatch, isShiftNotStartedYet, isShiftStartedLessThan30Minutes, onReportIncidentClick]);

  const menuItems = useMemo<DentalOfficeShiftCardMenuItem[]>(() => {
    const items: DentalOfficeShiftCardMenuItem[] = [
      { label: 'Chat', onSelect: () => browserHistory.push(`/dashboard/chat/${jobId}`) },
    ];
    if (canRebook) {
      items.push({ label: 'Rebook', onSelect: () => newShift() });
    }
    // Only offer "Add to Favorites" when the professional is not already a favorite.
    if (professionalId && !isFavorite) {
      items.push({ label: 'Add to Favorites', onSelect: addFavorite });
    }
    return items;
  }, [jobId, canRebook, newShift, professionalId, isFavorite, addFavorite]);

  return {
    menuItems,
    rebookPopup,
    onReportIncident: handleReportIncident,
    incidentReported: isReported,
    // Interactive (hover accent + tooltip) once the shift has started — mirrors the legacy
    // enabled state. The 30-min window gates the report action on click, not the icon.
    canReportIncident: !isShiftNotStartedYet,
  };
};
